import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Badge,
} from "@eduviaos/ui-kit";
import type { FunnelSuggestion } from "@eduviaos/shared";

import type { SuggestionSelection } from "../hooks/useSuggestionSelection.js";
import type { SelectableNode } from "../lib/collectNodes.js";
import { AIFormFieldDiff } from "./AIFormFieldDiff.js";
import { AIStageDiff } from "./AIStageDiff.js";

/**
 * L1 — a whole proposed funnel (Phase F-finale-4 Slice 5).
 *
 * Renders the funnel name as a header, every stage inside an expandable
 * accordion (all stages open by default so reviewers see everything before
 * accepting), and any bundled form fields under a dedicated section. Each
 * stage / requirement / field is independently selectable.
 */
export interface AIFunnelDiffProps {
  funnel: FunnelSuggestion;
  selection: SuggestionSelection;
}

function fallbackNode(nodeId: string): SelectableNode {
  return { node_id: nodeId, isValid: true };
}

/** Count of selected leaf nodes (stage + its requirements) for a stage header. */
function selectedInStage(
  funnelStage: FunnelSuggestion["stages"][number],
  selection: SuggestionSelection,
): { selected: number; total: number } {
  const ids = [funnelStage.node_id, ...funnelStage.requirements_for_stage.map((r) => r.node_id)];
  return {
    selected: ids.filter((id) => selection.isSelected(id)).length,
    total: ids.length,
  };
}

export function AIFunnelDiff({ funnel, selection }: AIFunnelDiffProps) {
  const allStageValues = funnel.stages.map((s) => s.node_id);

  return (
    <div className="space-y-4">
      <h3 className="text-base font-semibold">{funnel.funnel_name}</h3>

      <Accordion type="multiple" defaultValue={allStageValues}>
        {funnel.stages.map((stage) => {
          const counts = selectedInStage(stage, selection);
          return (
            <AccordionItem key={stage.node_id} value={stage.node_id}>
              <AccordionTrigger>
                <span className="flex items-center gap-2">
                  {stage.stage.label}
                  <Badge variant="secondary">
                    {counts.selected}/{counts.total} selected
                  </Badge>
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <AIStageDiff
                  stage={stage}
                  selection={selection}
                  stageLabel={`Include the ${stage.stage.label} stage`}
                />
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>

      {funnel.fields.length > 0 ? (
        <section className="space-y-1">
          <h4 className="text-muted-foreground text-xs font-semibold uppercase tracking-wide">
            New form fields
          </h4>
          {funnel.fields.map((field) => (
            <AIFormFieldDiff
              key={field.node_id}
              field={field}
              node={selection.nodeById.get(field.node_id) ?? fallbackNode(field.node_id)}
              checked={selection.isSelected(field.node_id)}
              onToggle={() => selection.toggle(field.node_id)}
            />
          ))}
        </section>
      ) : null}
    </div>
  );
}
